import { useEffect, useRef, useState } from "react";
import { Link } from "@tanstack/react-router";
import type { Citation, ConversationSummary } from "@backstory/protocol";
import { formatClock, formatRelative } from "../../../lib/format";
import { AutoLoadMore, ConfirmDialog, RowMenu } from "../../../components/ui";
import { askWhy, groupTools, useAskWhy, type ChatCitation, type ChatMessage } from "./store";
import { useAiCapability, useTranscript, resolveCitation } from "../hooks";
import { AiBlockedTooltip, AiOutput } from "../components/common";
import { AnswerText } from "../components/answer";
import "../ai.css";

/** Floating button that opens the drawer with an optional context. */
export function AskWhyButton({ context }: { context?: Parameters<typeof askWhy.open>[0] }) {
  const s = useAskWhy();
  const ai = useAiCapability();
  if (s.open) return null;
  return (
    <AiBlockedTooltip ai={ai}>
      <button type="button" className="askwhy-fab" onClick={() => askWhy.open(context)} aria-label="Ask Why" disabled={ai.blocked} title={ai.titleFor()} data-testid="askwhy-fab">
        Ask Why
      </button>
    </AiBlockedTooltip>
  );
}

function CitationLinks({ citations, sessionId, seek }: { citations: ChatCitation[]; sessionId?: string; seek?: (relMs: number) => void }) {
  const transcript = useTranscript(sessionId, !!sessionId);
  if (!citations.length) return null;
  return (
    <span className="cite-row" aria-label="Citations">
      {citations.map((c, i) => {
        const target = c.sessionId ?? sessionId;
        const t = resolveCitation(c as Citation, transcript.data);
        const label = t !== null ? formatClock(t) : c.stepId;
        if (target && target === sessionId && seek && t !== null) {
          return (
            <button key={i} type="button" className="cite-chip" onClick={() => seek(t)} data-testid="chat-cite">
              {label}
            </button>
          );
        }
        if (target?.startsWith("trace:")) {
          const traceId = target.slice(6);
          return (
            <Link key={i} className="cite-chip" to="/backend/traces/$traceId" params={{ traceId }} title="Open the backend request this answer cites" data-testid="chat-cite">
              request {traceId.slice(0, 6)}…
            </Link>
          );
        }
        if (target) {
          return (
            <Link key={i} className="cite-chip" to="/sessions/$id" params={{ id: target }} search={(t !== null ? { t: Math.round(t) } : {}) as never} data-testid="chat-cite">
              {target.slice(0, 6)}… {label}
            </Link>
          );
        }
        return (
          <span key={i} className="cite-chip" aria-disabled="true">
            {c.stepId}
          </span>
        );
      })}
    </span>
  );
}

/**
 * Shown until the first token lands. A tool chip stops moving the moment its
 * result is in, so the longest part of a turn — the model composing the answer
 * after the evidence is loaded — looked like a drawer that had given up.
 */
function Thinking() {
  return (
    <span className="thinking" role="status" aria-label="Thinking…">
      <i aria-hidden="true" />
      <i aria-hidden="true" />
      <i aria-hidden="true" />
    </span>
  );
}

function Message({ m, sessionId, seek, pending }: { m: ChatMessage; sessionId?: string; seek?: (relMs: number) => void; pending?: boolean }) {
  const content = (
    <>
      {m.tools.length > 0 && (
        <div className="tools">
          {groupTools(m.tools).map((t, i) => (
            <span key={i} className={`tool-chip${t.done ? "" : " running"}`} title={t.summary}>
              {t.label}
            </span>
          ))}
        </div>
      )}
      {m.role === "assistant" ? <AnswerText text={m.text} /> : m.text}
      {pending && <Thinking />}
      {m.role === "assistant" && <CitationLinks citations={m.citations} sessionId={sessionId} seek={seek} />}
      {m.error && <span className="error-box" style={{ display: "block", marginTop: 6 }}>{m.error}</span>}
    </>
  );
  if (m.role === "assistant") {
    return (
      <AiOutput className="msg assistant" testId="msg-assistant" compact>
        {content}
      </AiOutput>
    );
  }
  return (
    <div className="msg user" data-testid="msg-user">
      {content}
    </div>
  );
}

/**
 * The conversation. Beside a session or an issue it is a pullout. On the Ask
 * Why page it fills the page, because there is nothing behind it to keep in view.
 */
export function AskWhyDrawer({ seek, variant = "drawer" }: { seek?: (relMs: number) => void; variant?: "drawer" | "page" }) {
  const s = useAskWhy();
  const ai = useAiCapability();
  const [text, setText] = useState("");
  const [historyOpen, setHistoryOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<ConversationSummary | null>(null);
  const [deleting, setDeleting] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);
  /** Distance from the bottom to hold on to while an older page is prepended. */
  const anchor = useRef<number | null>(null);

  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    const keep = anchor.current;
    if (keep !== null) {
      // Older messages arriving above the reader must not move what they read.
      anchor.current = null;
      el.scrollTop = el.scrollHeight - keep;
      return;
    }
    if (typeof el.scrollTo === "function") el.scrollTo({ top: el.scrollHeight });
  }, [s.messages]);
  useEffect(() => {
    if (s.open) void askWhy.restore();
  }, [s.open, s.context]);
  useEffect(() => {
    if (!s.open || variant === "page") return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && askWhy.close();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [s.open, variant]);

  if (!s.open) return null;
  const submit = () => {
    const t = text;
    setText("");
    void askWhy.send(t);
  };
  const ctx = s.context;
  const loadOlder = () => {
    if (!s.olderCursor || s.loadingOlder) return;
    const el = listRef.current;
    anchor.current = el ? el.scrollHeight - el.scrollTop : null;
    void askWhy.loadOlder();
  };
  const startNew = () => {
    setHistoryOpen(false);
    askWhy.newConversation();
  };
  const openChat = (id: string) => {
    setHistoryOpen(false);
    void askWhy.openConversation(id);
  };

  return (
    <aside className={`${variant === "page" ? "askwhy-page" : "askwhy-drawer"}${historyOpen ? " show-history" : ""}`} role={variant === "page" ? "region" : "dialog"} aria-label="Ask Why" data-testid="askwhy-drawer">
      {variant === "drawer" && (
        <div className="askwhy-header">
          <span className="badge ai">Why</span>
          <h2>Ask Why</h2>
          <button type="button" className="btn ghost" onClick={() => askWhy.close()} aria-label="Close">✕</button>
        </div>
      )}
      <div className="askwhy-body">
      <div className="askwhy-history" aria-label="Earlier chats">
        <div className="askwhy-history-head">
          <button type="button" className="btn small" onClick={startNew} data-testid="chat-new">New chat</button>
          <button type="button" className="btn ghost small history-back" onClick={() => setHistoryOpen(false)}>Back</button>
        </div>
        <div className="askwhy-history-list">
          {s.historyLoading && s.history.length === 0 && (
            <div aria-busy="true" aria-label="Loading chats">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="askwhy-history-row">
                  <span className="skeleton" style={{ height: 14, width: `${70 - i * 8}%`, margin: "10px 8px" }}>loading</span>
                </div>
              ))}
            </div>
          )}
          {s.history.length === 0 && !s.historyLoading && <p className="small muted askwhy-history-empty">No earlier chats.</p>}
          {s.history.map((chat) => (
            <div key={chat.id} className={`askwhy-history-row${chat.id === s.conversationId ? " active" : ""}`}>
              <button type="button" className="askwhy-history-open" aria-current={chat.id === s.conversationId ? "true" : undefined} onClick={() => openChat(chat.id)}>
                <span className="askwhy-history-title">{chat.title}</span>
                <span className="small muted">{formatRelative(chat.updatedAt)}</span>
              </button>
              <RowMenu label={`Actions for ${chat.title}`}>
                <button type="button" role="menuitem" className="danger" onClick={() => setPendingDelete(chat)}>Delete chat</button>
              </RowMenu>
            </div>
          ))}
          <AutoLoadMore hasMore={!!s.historyCursor} loading={s.historyLoading && s.history.length > 0} loadMore={() => void askWhy.loadMoreHistory()} label="Loading earlier chats" />
        </div>
        <p className="small muted askwhy-history-note">Chats idle for 30 days are removed.</p>
      </div>
      <div className="askwhy-thread">
      <div className="askwhy-toolbar">
        <button type="button" className="btn ghost small history-toggle" onClick={() => setHistoryOpen(true)} data-testid="chat-history">History</button>
        <button type="button" className="btn small" onClick={startNew}>New chat</button>
      </div>
      {(ctx.sessionId || ctx.clusterId || ctx.fromMs !== undefined) && (
        <div className="askwhy-context">
          <span>Context:</span>
          {ctx.sessionId && <span className="badge neutral">{ctx.label ?? `session ${ctx.sessionId.slice(0, 8)}…`}</span>}
          {ctx.clusterId && <span className="badge neutral askwhy-context-label" title={ctx.label}>{ctx.label ?? `issue ${ctx.clusterId.slice(0, 10)}…`}</span>}
          {ctx.fromMs !== undefined && ctx.toMs !== undefined && <span className="badge neutral">{formatClock(ctx.fromMs)} – {formatClock(ctx.toMs)}</span>}
        </div>
      )}
      <div
        className="askwhy-messages"
        ref={listRef}
        onScroll={(e) => {
          if (e.currentTarget.scrollTop < 48) loadOlder();
        }}
      >
        {/* Scrolling up is the usual way in, but the strip keeps the reach
            explicit for keyboard and for a list too short to scroll. */}
        {s.olderCursor && (
          <div className="askwhy-older">
            {s.loadingOlder ? (
              <span className="small muted" role="status">Loading earlier messages…</span>
            ) : (
              <button type="button" className="btn ghost small" onClick={loadOlder}>Earlier messages</button>
            )}
          </div>
        )}
        {s.restoring && s.messages.length === 0 && <p className="small muted" role="status">Looking for your earlier questions…</p>}
        {s.messages.length === 0 && !s.restoring && (
          <p className="small muted">
            {variant === "page"
              ? "Try: “Which sessions failed to submit the intake form this week?” · “Why did checkout INP regress after release 4.12.3?” · “Show me rage clicks on the Save button.”"
              : "Ask anything grounded in your recordings."}
          </p>
        )}
        {s.messages.map((m, i) => (
          <Message
            key={m.id}
            m={m}
            sessionId={ctx.sessionId}
            seek={seek}
            pending={s.streaming && m.role === "assistant" && i === s.messages.length - 1 && !m.text && !m.error}
          />
        ))}
        {s.error && !s.messages.some((m) => m.error) && <div className="error-box" role="alert">{s.error}</div>}
      </div>
      <form
        className="askwhy-form"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
        autoComplete="off"
      >
        <div className="askwhy-composer">
          <textarea
            className="input"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={ctx.sessionId ? "Ask about this session…" : ctx.clusterId ? "Ask about this issue…" : "Ask about your sessions…"}
            aria-label="Message"
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                submit();
              }
            }}
            disabled={s.streaming || ai.blocked}
            title={ai.titleFor()}
            autoComplete="off"
          />
          {s.streaming ? (
            <button type="button" className="askwhy-send" onClick={() => askWhy.stop()} aria-label="Stop" data-testid="chat-stop">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><rect x="6" y="6" width="12" height="12" rx="2" /></svg>
            </button>
          ) : (
            <button type="submit" className="askwhy-send" disabled={!text.trim() || ai.blocked} title={ai.titleFor() || "Send"} aria-label="Send" data-testid="chat-send">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 12 20 4l-8 16-2-6Z" /></svg>
            </button>
          )}
        </div>
      </form>
      </div>
      </div>
      <ConfirmDialog
        open={!!pendingDelete}
        danger
        title="Delete chat"
        confirmLabel="Delete chat"
        busy={deleting}
        onClose={() => { if (!deleting) setPendingDelete(null); }}
        onConfirm={() => {
          if (!pendingDelete || deleting) return;
          const id = pendingDelete.id;
          setDeleting(true);
          void askWhy.removeConversation(id).then(() => setPendingDelete(null)).finally(() => setDeleting(false));
        }}
      >
        <p>Delete “{pendingDelete?.title}”? This removes the whole conversation.</p>
      </ConfirmDialog>
    </aside>
  );
}
